export default {
  'practice-defining-problem': {
    'course-title': 'Practice in Defining Problems',
    'course-name': 'Section 5B <span class="unit-title">Practice in Defining Problems<span>',
    'course-video': {
      'title': 'Section 5 DEFINING THE PROBLEM<br/>1’58:28 – 2’05:31 5B VISUALIZING THE PROBLEM :<br/>-ex 5B laying out the parts of the problem',
      'src': 'https://www.youtube.com/embed/5aov7rXM3Vw?enablejsapi=1',
      'lecture-title': '5B Lecture'
    },
    'unit-section-title': '5B Exercises',
    'intro': `<p>Key to defining any problem is sorting out the various causes from the various effects that cling together when you try to describe it. And in most business problem situations, there are multiple causes and multiple effects that need to be sorted out and arranged logically before you see it clearly. Filling in the <strong>Problem Definition Framework</strong> is the simplest approach to do it that I know, because it is set up to make the problem instantly clear and easy to explain.</p>
    <br/>
<p>Here are three simple exercises to give you practice in sorting out the parts of a problem. I will give you the facts for each and lay out the Opening Scene. I will also list all the other elements of the structure, to make it easy for you to place them where they belong in the framework. These exercises are meant to help you feel at ease with the new framework and vocabulary, and to demonstrate how easily they translate into an SCQ introduction.</p>
<br/>
<p>In the next section you will practice on more complex problems, identifying more of the problem definition elements, before transforming them into SCQ introductions.</p>
<br/>`,
    'menu-list': {
      'exercise-1': 'The Sales Force Effectiveness Paradox',
      'exercise-2': 'Analysis of Technology Alternatives',
      'exercise-3': 'Advantage Survey – Results and Action Plan'
    },
    'exercise-1': {
      'number': '5B - Exercise 1',
      'title': 'The Sales Force Effectiveness Paradox',
      'text-1': `<p>Here is the opening paragraph of a long magazine article on making better use of sales force selling time in large manufacturing companies. It presents insights drawn from interviewing 25 CEOs of billion-dollar distributors who staff and manage large field sales forces. The introduction gives a good clear outline of the problem that the remainder of the article goes on to discuss more fully.</p>
      <br/>
      <p><strong>Please:</strong></p>
      <ol>
        <li>Read the article</li>
        <li>Look at the Problem Definition Framework</li>
        <li>Find the list of problem elements</li>
        <li>Drag each element to its proper slot in the framework</li>
        <li>See how they fit into the SCQ introduction to the pyramid for the full article</li>
      </ol>`,
      'blue-paper': `<h5><span>The Sales Force Effectiveness Paradox</span><br/><span>Why Sales Forces Still Produce 80% of their<br/>value from 20% of their activities.</span></h5>
    <br/>
    <p class="cite">“We have put a huge amount of investment into technology and enablers for the sales force, yet the sales productivity number is the same now as it was ten years ago.”</p>
    <p class="reference">- CEO, $Bn Disributor</p>
    <br/>
    <p>In our recent interviews with 25 CEOs of billion dollar distributors, we’ve heard a consistent theme: that sales force effectiveness is one of their highest priority issues, and that they are frustrated. Many have made substantial investments in a broad range of sales enablers such as sales force automation platforms, solution selling, challenger training, or in some cases large-scale ERP re-platforming programs.</p>
    <br/>
    <p>Yet an inconvenient truth remains... few companies have managed to ignite a profitable sales growth engine. Indeed, many CEOs tell us that their sales productivity metrics have remained largely unchanged for years. Furthermore, now more than ever, they see the need for real improvement to offset slow market growth and to out-sell their competitors, including new online players.</p>
    <br/>
    <p>In this paper, we talk about what’s behind this frustration and what to do about it. We’ll focus in our examples on the mass of mid-size customers that can make up 50–80%+ of a distributor’s revenue, since it is in that part of the business that this challenge is most acute. However, many of the same principles apply to larger “national accounts” or “strategic partnerships.”</p>`,
      'text-2': `<span>Here is the structure of the document you will write. Now let’s lay out the problem that will be the basis of your introduction.</span>
      <br/><br/>
      <div class="desc-scq-diagram">
            <div class="desc-scq-diagram__title">
                <p>The Sales Force Effectiveness Paradox</p>
            </div>
            <div class="scq-text">
                <div class="scq-block">
                    <p>S =</p>
                </div>
                <div class="scq-block">
                    <p>C =</p>
                </div>
                <div class="scq-block">
                    <p>Q =</p>
                </div>
            </div>
            <div class="scq-diagram">
                <div class="scq-block">
                    <p>Distributors need to re-shape their sales forces’ day-to-day activities to match each customer’s lifetime value</p>
                </div>
                <svg>
                    <line x1="450" y1="0" x2="85" y2="100"/>
                    <line x1="450" y1="0" x2="325" y2="100"/>
                    <line x1="450" y1="0" x2="575" y2="100"/>
                    <line x1="450" y1="0" x2="810" y2="100"/>
                </svg>
                <div class="scq-diagram-body">
                <div class="scq-block">
                    <p>Identify what specific customers and markets you want your sales growth to come from</p>
                </div>
                <div class="scq-block">
                    <p>Judge the structural potency of your proposition to th ose sources vs compe-titors’ offerings</p>
                </div>
                <div class="scq-block">
                    <p>Allocate sales capacity to match the lifetime value of the opportunity</p>
                </div>
                <div class="scq-block">
                    <p>Provide bespoke “by the field for the field” tools</p>
                </div>
                </div>
            </div>
        </div>
        <br/><br/>`
    },
    'intro-2': `<h6 class="intro__title">Full wording of the introduction</h6>
    <br/>
    
    <div class="intro__introduction">
    <p>As every distributor knows, increasing sales force productivity is the key to market growth and profitability. Consequently, they invest substantial amounts in sales enablers promising to improve that productivity. Nevertheless, to quote one CEO of a $Bn distributor, “The Sales productivity number is the same now as it was 10 years ago.”
<br/><br/>
But given slow market growth and the advent of online sellers, increased productivity is more important than ever. This is particularly true with mid-size customers, who make up 50-80% of a distributor’s revenue.
<br/><br/>
We have been examining this problem, in interviews with 25 CEOs of billion dollar distributors. We have focused on trying to pinpont the reasons for low productivity, to identify what can be done to ignite a sales growth engine within the sales force. We believe the secret to increasing that productivity lies in determing each customer’s lifetime value, and reshaping the sales force’s day-to-day activities to match.</p>
    </div>`,
    'exercise-2': {
      'number': '5B - Exercise 2',
      'title': 'Analysis of Technology Alternatives',
      'text-1': `<p>Assume that you work for a Bank, and are responsible for running its current CIF and deposit systems. (CIF stands for the Bank’s Customer Information File number, which enables it to view customers’ accounts by their total bank relationship in addition to account type). You need to write a memorandum recommending that the Bank outsource the running of these two systems. However, you know that management has stated categorically that it does not want to outsource these systems.</p>`,
      'blue-paper': `<h5>Here is what you know</h5>
        <div class="separator"></div>
    <br/>
    <p>At the moment the CIF and Deposit systems run on Unisys Clearpath mainframes, at a cost of $12 million/year. The hardware is becoming obsolete, and the systems have a life of only 3-4 more years. Management agrees that the Bank must change its approach to technology delivery or risk becoming competitively disadvantaged in the marketplace.</p>
    <br/>
    <p>The bank has three options: upgrade, migrate to a new system, or outsource.. Management has said it would rather not outsource, in the belief that an outside firm would not be able to maintain the high level of data protection service the Bank desires. You have been asked to look into the other two alternatives and recommend the Bank’s strategy.</p>
    <br/>
    <p>Unfortunately, you have concluded that neither upgrading nor migrating makes much sense for them, but outsourcing does. Accordingly, you have decided to bite the bullet and recommend outsourcing for the following reasons:</p>
    <ul class="bullet-list">
      <li>Upgrading will not address the present system’s deficiencies</li>
      <li>Migrating to a turn-key system would be high cost, without solving all of the problems</li>
      <li>Outsourcing provides a better product at a cost much lower than the present $12m</li>
      <li>Two highly rated vendors are available to do the job</li>
    </ul>`,
      'text-2': `<span>Here is the structure of the document you will write. Now let’s lay out the problem that will be the basis of your introduction.</span>
      <br/><br/><div class="desc-scq-diagram">
            <div class="scq-text">
                <div class="scq-block">
                    <p>S =</p>
                </div>
                <div class="scq-block">
                    <p>C =</p>
                </div>
                <div class="scq-block">
                    <p>Q =</p>
                </div>
            </div>
            <div class="scq-diagram">
                <div class="scq-block">
                    <p>We should think seriously about negotiating to develop an outsourcing relationship</p>
                </div>
                <svg>
                    <line x1="450" y1="0" x2="85" y2="100"/>
                    <line x1="450" y1="0" x2="325" y2="100"/>
                    <line x1="450" y1="0" x2="575" y2="100"/>
                    <line x1="450" y1="0" x2="810" y2="100"/>
                </svg>
                <div class="scq-diagram-body">
                <div class="scq-block">
                    <p>Upgrading will not address the present system's deficiencies</p>
                </div>
                <div class="scq-block">
                    <p>Migrating to a new system would be high cost, without solving all of the problems</p>
                </div>
                <div class="scq-block">
                    <p>Outsourcing provides a better product at a cost much lower than the present $12m</p>
                </div>
                <div class="scq-block">
                    <p>Two highly rated vendors are available to do the job</p>
                </div>
                </div>
            </div>
        </div>`,
      'intro-2': `<h6 class="intro__title">Full wording of the introduction</h6>
    <br/>
    <div class="intro__introduction">
        <p>We currently process our customer information on Unisys Clearpath Dorado HW purchased 2 years ago, but is now becoming obsolete. The engineers tell us they will cease supporting it in 3-4 years. We must decide our strategy for the future.</p>
        <br/>
        <p>We have essentially three options: we can upgrade to another Unisys system, migrate to a new system, or outsource the activity. Management has indicated that it does not wish to outsource. You have asked me to recommend the appropriate alternative.</p>
        <br/>
        <p>Despite the reluctance to outsource, I recommend that we think seriously about negotiating to develop a strong outsourcing relationship.</p>
        <ul>
            <li>Upgrading will not address the present systems deficiencies</li>
            <li>Migrating to a new system would be high cost, without solving all of the problems</li>
            <li>Outsourcing provides a better product at a cost much lower than the current $12m expense</li>
        </ul>
        <p>If you approve, we have located two highly rated vendors to consider for the job.</p>
    </div>
    <br/><br/>`
    },
    'exercise-3': {
      'number': '5B - Exercise 3',
      'title': 'Advantage Survey Results and Action Plan',
      'text-1': `<p>S&G Wholesale Grocers is a large east coast US wholesale grocery packaging and distribution company. S&G owns a subsidiary supermarket chain called Winters, which has just been the subject of an annual survey of its performance by a company called Advantage International. Advantage has annually since 2007 surveyed the performance of grocers and distributors throughout the country, to rate them against each other. Its most recent survey questioned 47 manufacturers about Winters and its 6 major competitors.</p>
                <br/><br/>
                <p>Over the last 3 years, S&G had been the leading performer against the others, so it was not happy to learn that it ranked only #2 in overall performance against its competitors.</p>`,
      'text-2': `<br/><br/><p>Assume you have been charged with putting together a presentation to assure management that vigorous action is being taken to maintain S&G's #2 overall position, and work to approach #1. Here is the structure of the document you will write. Now let’s lay out the problem that will be the basis of your introduction.</p>
      <br/><br/>
      <div class="desc-scq-diagram">
            <div style="max-width: 340px" class="desc-scq-diagram__title">
                <p>SUGGESTED STRUCTURE 2014 ADVANTAGE SURVEY RESULTS AND ACTION PLAN</p>
            </div>
            <div class="scq-text">
                <div class="scq-block">
                    <p>S =</p>
                </div>
                <div class="scq-block">
                    <p>C =</p>
                </div>
                <div class="scq-block">
                    <p>Q =</p>
                </div>
            </div>
            <div class="scq-diagram">
                <div class="scq-block">
                    <p>They will be focusing on how to make it easier for customers and mfrs. to do business with S&G at every level</p>
                </div>
                <svg>
                    <line x1="450" y1="0" x2="85" y2="100"/>
                    <line x1="450" y1="0" x2="325" y2="100"/>
                    <line x1="450" y1="0" x2="575" y2="100"/>
                    <line x1="450" y1="0" x2="810" y2="100"/>
                </svg>
                <div class="scq-diagram-body">
                <div class="scq-block">
                    <p>Eliminating the specific areas of discontent in our Procurement, Merchandising, and Bill Paying processes</p>
                </div>
                <div class="scq-block">
                    <p>Making clear to all users the process for decision-making and points of contact in each area</p>
                </div>
                <div class="scq-block">
                    <p>Defining the Category Management work that C&S offers, and selling our successes to the stores</p>
                </div>
                <div class="scq-block">
                    <p>Tying our merchandising programs directly to customer performance</p>
                </div>
                </div>
                
                <div class="scq-diagram-body scq-diagram-body__list">
                    <ul>
                        <li>Strategic Partnership Program</li>
                        <li>Vendor program value proposition</li>
                        <li>Improve our current communication practices</li>
                        <li>National merchandising program</li>
                    </ul>
                    <ul>
                        <li>Protocol for decision making within procurement</li>
                        <li>Key Contact List for program manufacturers</li>
                        <li>Engagement of non-procurement key contacts</li>
                    </ul>
                    <ul>
                        <li>Clearly define and communic. the category mgt. work that C&S undertakes</li>
                        <li>Sell our successes</li>
                    </ul>
                    <ul>
                        <li>Drive volume through the merchandising vehicles</li>
                        <li>Merchandising programs that are tied to customer performance</li>
                        <li>Continue to develop the national merchandising programs</li>
                    </ul>
                </div>
            </div>
        </div>`,
    },
    'intro-3': `<h6 class="intro__title">Full wording of the introduction</h6>
    <br/>
    <div class="intro__introduction">
        <p>Have taken part in Advantage International’s annual survey of the Grocery industry. Results for 2014 are now in, and find that – while we remain #2 in overall performance - we have fallen well behind in each of the other measures (see chart).</p>
        <br/>
        <p>Have identified the likely causes of the low ratings, and set up teams to determine the steps we need to take to regain our #2 position in all areas, and will work to approach #1.</p>
        <br/>
        <p>The teams will</p>
        <ul>
            <li>Improve working relationships with manufacturers</li>
            <li>Ensure S&G points of contact are communicated to the vendor/broker community</li>
            <li>Provide effective category management and marketing solutions to both customers and manufacturers</li>
            <li>Tie our merchandising programs directly to customer performance</li>
        </ul>
    </div>
    <br/><br/>`,
    'congratulation-message': 'CONGRATULATIONS',
    'finish-section': 'You have completed Section 5B',
    'final-description': 'You have first-hand experience in working out the<br/>parts of a problem within a structured framework',
    'next-course-button': 'proceed to section 5C',
    'dashboard-button': 'Main menu',
  }
}



// WEBPACK FOOTER //
// ./client/app/modules/courses/5/5b/practiceDefiningProblem/i18n/en.js